import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpandMore } from '@material-ui/icons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@material-ui/core';

import Spinner from '../spinner';
import Text from '../../containers/Text';
import YeulsTable from '../yeuls-table';
import { getAuthHistory } from '../../api/requests/auth-history';
import { getErrorMessage } from '../../shared/services/error-handler';

import {
  LOGIN_TABLE_COLUMNS,
  SERVER_ERROR_MESSAGE_KEYS,
  TRANSLATION_KEYS,
} from './constants';
import {
  formatDateTime,
  getCurrentMonthDateRange,
  getUserFullName,
} from './helpers';
import {
  buildRangeLabel,
  isValidDateRange,
} from '../email-tracking/helpers';

import './AuthHistory.scss';

const INITIAL_ERROR_STATE = { key: '', fallback: '' };
const INITIAL_MONTH_RANGE = getCurrentMonthDateRange();

const DateRangeInput = ({ id, labelKey, value, onChange, t, min, max }) => (
  <label className="date-input-label" htmlFor={id}>
    <Text text={t(labelKey)} />
    <input
      id={id}
      type="date"
      className="date-input"
      value={value}
      min={min}
      max={max}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

const UserLoginsTable = ({ logins, t, language }) => {
  const headers = useMemo(
    () => [t(TRANSLATION_KEYS.colDate), t(TRANSLATION_KEYS.colDeviceInfo)],
    [t, language],
  );

  const mappedLogins = useMemo(
    () =>
      logins.map((login) => ({
        ...login,
        createdAtView: (
          <span className="auth-history-datetime" dir="ltr">
            {formatDateTime(login.createdAt)}
          </span>
        ),
        deviceInfoView: login.deviceInfo || '',
      })),
    [logins],
  );

  return (
    <YeulsTable
      data={mappedLogins}
      headers={headers}
      columns={LOGIN_TABLE_COLUMNS}
      totalCount={mappedLogins.length}
      perPageItemCount={12}
      partialPageCount={3}
      nextPageText=">"
      prePageText="<"
      className="auth-history-table-container"
    />
  );
};

const UserAuthAccordion = ({ userGroup, t, language }) => {
  const fullName = getUserFullName(userGroup.user);
  const lastLoginLabel = t(TRANSLATION_KEYS.lastLogin);
  const loginsCountLabel = t(TRANSLATION_KEYS.loginsCount);

  return (
    <div className="auth-history-accordion-wrapper">
      <Accordion className="auth-history-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <div className="auth-history-accordion-title">
            <Text text={fullName} />
            {userGroup.user?.email ? (
              <>
                <span className="auth-history-title-separator">—</span>
                <Text text={userGroup.user.email} />
              </>
            ) : null}
            <span className="auth-history-title-separator">—</span>
            <Text text={`${loginsCountLabel}: ${userGroup.loginsCount}`} />
            <span className="auth-history-title-separator">—</span>
            <Text text={`${lastLoginLabel}:`} />
            <span className="auth-history-datetime" dir="ltr">
              {formatDateTime(userGroup.lastLoginAt)}
            </span>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className="auth-history-table-wrapper">
            <UserLoginsTable
              logins={userGroup.logins}
              t={t}
              language={language}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

const AuthHistoryReport = () => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [errorState, setErrorState] = useState(INITIAL_ERROR_STATE);
  const [users, setUsers] = useState([]);
  const [range, setRange] = useState(INITIAL_MONTH_RANGE);
  const [fromDate, setFromDate] = useState(INITIAL_MONTH_RANGE.from);
  const [toDate, setToDate] = useState(INITIAL_MONTH_RANGE.to);

  const errorMessage = errorState.key ? t(errorState.key) : errorState.fallback;
  const hasRange = Boolean(range.from && range.to);
  const hasUsers = users.length > 0;

  const handleLoadError = (err) => {
    const serverMessage = String(getErrorMessage(err, '') || '').trim().toLowerCase();
    const errorKey = SERVER_ERROR_MESSAGE_KEYS[serverMessage];

    setErrorState({
      key: errorKey || TRANSLATION_KEYS.somethingWentWrong,
      fallback: '',
    });
  };

  const loadReportByRange = async (selectedFrom = fromDate, selectedTo = toDate) => {
    if (!selectedFrom || !selectedTo) {
      setErrorState({ key: TRANSLATION_KEYS.selectFromToDates, fallback: '' });
      return;
    }

    if (!isValidDateRange(selectedFrom, selectedTo)) {
      setErrorState({ key: TRANSLATION_KEYS.invalidRange, fallback: '' });
      return;
    }

    setIsLoading(true);
    setErrorState(INITIAL_ERROR_STATE);

    try {
      const selectedRange = { from: selectedFrom, to: selectedTo };
      const response = await getAuthHistory(selectedRange);
      const reportResult = response?.data?.result || {};

      setRange({
        from: reportResult.from || selectedRange.from,
        to: reportResult.to || selectedRange.to,
      });
      setUsers(reportResult.users || []);
    } catch (err) {
      handleLoadError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReportByRange(INITIAL_MONTH_RANGE.from, INITIAL_MONTH_RANGE.to);
    // Load current month once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="auth-history-container">
      <div>
        <div className="title">
          <Text text={t(TRANSLATION_KEYS.reportTitle)} />
        </div>
      </div>

      <div className="auth-history-actions">
        <div className="date-range-inputs">
          <DateRangeInput
            id="auth-history-from-date"
            labelKey="from"
            value={fromDate}
            onChange={setFromDate}
            max={toDate || undefined}
            t={t}
          />
          <DateRangeInput
            id="auth-history-to-date"
            labelKey="to"
            value={toDate}
            onChange={setToDate}
            min={fromDate || undefined}
            t={t}
          />
        </div>

        <button
          type="button"
          className="load-report-button"
          onClick={() => loadReportByRange()}
          disabled={isLoading}
        >
          <Text text={t(TRANSLATION_KEYS.loadReport)} />
        </button>
      </div>

      {hasRange && (
        <div className="selected-range">
          <Text text={buildRangeLabel(range, i18n.language, t)} />
        </div>
      )}

      {errorMessage !== '' && (
        <div className="error-message">
          <Text text={errorMessage} />
        </div>
      )}

      {isLoading && <Spinner />}

      {!isLoading && hasUsers && (
        <div className="auth-history-users-list">
          {users.map((userGroup) => (
            <UserAuthAccordion
              key={userGroup.user?.id || userGroup.lastLoginAt}
              userGroup={userGroup}
              t={t}
              language={i18n.language}
            />
          ))}
        </div>
      )}

      {!isLoading && !hasUsers && hasRange && (
        <div className="no-records-message">
          <Text text={t(TRANSLATION_KEYS.noRecordsFound)} />
        </div>
      )}
    </div>
  );
};

export default AuthHistoryReport;
